import {
  AssetDto,
  AssetGroupDto,
  AssetPriceChangesDto,
  CandleDto,
  CoinDto,
  CoinMarketCapCategoryDto,
  FavoriteAssetDto,
  FavoriteCoinDto,
} from 'common/api/autogenerated/trading-gateway';

import { AssetDtoEnhanced, AssetsHashMap, AssetTrendType, CoinGroupsHashMap } from 'common/types/assets';

export const mapAssetsFromServer = (assets: AssetDto[] = []): AssetsHashMap =>
  assets.reduce<Record<string, AssetDtoEnhanced>>((result, current) => {
    if (current.name) {
      return {
        ...result,
        [current.name]: {
          ...current,
          name: current.name,
          trend: [],
          price: Number(current.lastPrice),
          prevPrice: Number(current.lastPrice),
          prevBid: current.bid,
          prevAsk: current.ask,
          symbol: current.name,
          code: `${current.currency}-${current.quoteCurrency}`,
          baseCurrency: current.currency,
          quoteCurrency: current.quoteCurrency,
        },
      };
    }
    return result;
  }, {});

export const mapCoinsToAssets = (coins: CoinDto[] = []): AssetsHashMap =>
  coins.reduce<Record<string, AssetDtoEnhanced>>((result, current) => {
    if (current.name) {
      return {
        ...result,
        [current.name]: {
          ...current,
          dailyTradeVolume: current.volume24H,
          currency: current.baseCurrency,
          displayName: current.description,
          trend: [],
          price: Number(current.lastPrice),
          prevPrice: Number(current.lastPrice),
          prevBid: current.bid,
          prevAsk: current.ask,
          symbol: current.symbol,
          code: current.symbol,
          categories: current.categories,
        },
      };
    }
    return result;
  }, {});

export const mapGroupsFromServer = (favorites: AssetGroupDto[]) =>
  favorites.reduce<Record<string, AssetGroupDto>>((result, group) => {
    if (group.type) {
      return {
        ...result,
        [group.type]: group,
      };
    }
    return result;
  }, {});

export const mapFavoriteAssetsFromServer = (favorites: FavoriteAssetDto[]): string[] =>
  favorites.reduce<string[]>((result, favorite) => {
    if (favorite.symbol) {
      return [...result, favorite.symbol];
    }
    return result;
  }, []);

export const mapFavoriteCoinsFromServer = (favorites: FavoriteCoinDto[]): string[] =>
  favorites.reduce<string[]>((result, favorite) => {
    if (favorite.coin) {
      return [...result, favorite.coin];
    }
    return result;
  }, []);

export const applyTrendToAsset = (trends: AssetTrendType, assets: AssetsHashMap) => {
  const data = assets;

  const convertCandleToTrend = (candle: CandleDto[]) => candle.reverse().map((x) => x.closed || 0);

  Object.keys(trends).forEach((item) => {
    data[item] = {
      ...data[item],
      trend: convertCandleToTrend(trends[item]),
    };
  });

  return data;
};

export const convertAssetsToKeys = (list: Array<{ name?: string | null }> | null | undefined): string[] =>
  (list ?? []).map(({ name }) => name ?? '');

export const syncPricesChanges = (price: number, pricesChanges: AssetPriceChangesDto = {}): AssetPriceChangesDto => ({
  ...pricesChanges,
  day: {
    ...(pricesChanges.day || {}),
    low: price < Number(pricesChanges.day?.low) ? price : Number(pricesChanges.day?.low),
    high: price > Number(pricesChanges.day?.high) ? price : Number(pricesChanges.day?.high),
  },
  month: {
    ...(pricesChanges.month || {}),
    low: price < Number(pricesChanges.month?.low) ? price : Number(pricesChanges.month?.low),
    high: price > Number(pricesChanges.month?.high) ? price : Number(pricesChanges.month?.high),
  },
  year: {
    ...(pricesChanges.year || {}),
    low: price < Number(pricesChanges.year?.low) ? price : Number(pricesChanges.year?.low),
    high: price > Number(pricesChanges.year?.high) ? price : Number(pricesChanges.year?.high),
  },
});

export const mapCoinsGroupsFromServer = (coinCategories: CoinMarketCapCategoryDto[]): CoinGroupsHashMap =>
  coinCategories.reduce<CoinGroupsHashMap>((_groupsMap, coinCategory) => {
    if (coinCategory.name) {
      _groupsMap[coinCategory.name] = {
        type: coinCategory.name,
        isDefault: false,
        label: coinCategory.name,
      };
    }
    return _groupsMap;
  }, {});
